import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ApiError, api, unwrap, type Schemas } from './api/client.js';
import { isLanguage, setLanguage } from './i18n.js';

export type Me = {
  user: Schemas['User'];
  account: Schemas['Account'];
  session: { mfaVerified: boolean };
};

export const ME_KEY = ['me'] as const;

/** The signed-in user, or null when there is no session. */
export function useMe() {
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<Me | null> => {
      try {
        return (await unwrap(api.GET('/me'))) as Me;
      } catch (error) {
        if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
  // The user's language wins over the browser's once we know it.
  const locale = query.data?.user.locale;
  useEffect(() => {
    if (isLanguage(locale)) setLanguage(locale);
  }, [locale]);
  return query;
}

export function useSignOut() {
  const client = useQueryClient();
  return async () => {
    await api.POST('/auth/logout').catch(() => undefined);
    client.setQueryData(ME_KEY, null);
    client.clear();
  };
}
